<template>
  <div>
    <v-menu v-model="menu" :close-on-content-click="false" offset-x left transition="scale-transition" origin="top right">
      <template v-slot:activator="{ on, attrs }">
        <button class="ec-icon-btn ec-icon-btn--primary" v-bind="attrs" v-on="on" title="User actions">
          <v-icon size="16">mdi-account-cog-outline</v-icon>
        </button>
      </template>

      <div class="ua-menu">
        <!-- User info -->
        <div class="ua-menu__header">
          <div class="ua-avatar">{{ (userSource.name || userSource.email || '?').charAt(0).toUpperCase() }}</div>
          <div class="ua-menu__info">
            <p class="ua-menu__name">{{ userSource.name || '—' }}</p>
            <p class="ua-menu__email">{{ userSource.email }}</p>
          </div>
        </div>

        <div class="ua-menu__body">
          <!-- Active toggle -->
          <div class="ua-toggle-row">
            <div>
              <p class="ua-toggle-label">Active</p>
              <p class="ua-toggle-hint">Can sign in and use the platform</p>
            </div>
            <v-switch v-model="isActive" color="success" inset dense hide-details class="ua-switch" />
          </div>
          <!-- Block toggle -->
          <div class="ua-toggle-row">
            <div>
              <p class="ua-toggle-label">Blocked</p>
              <p class="ua-toggle-hint">Prevent platform access</p>
            </div>
            <v-switch v-model="isBlocked" color="error" inset dense hide-details class="ua-switch" />
          </div>
        </div>

        <div class="ua-menu__footer">
          <button v-if="!userSource.deleted" class="ua-danger-btn" @click="deleteUser(userSource.id)">
            <v-icon size="13" style="margin-right:4px">mdi-delete-outline</v-icon>Delete
          </button>
          <button v-else class="ua-restore-btn" @click="restoreUser(userSource.id)">
            <v-icon size="13" style="margin-right:4px">mdi-arrow-left-top</v-icon>Restore
          </button>
          <div style="display:flex;gap:8px">
            <button class="ec-btn ec-btn--ghost" style="padding:6px 12px;font-size:.8rem" @click="menu = false">Cancel</button>
            <button class="ec-btn ec-btn--primary" style="padding:6px 12px;font-size:.8rem"
                    :disabled="userSource.blocked === isBlocked && userSource.is_active === isActive"
                    @click="saveChanges">Save</button>
          </div>
        </div>
      </div>
    </v-menu>

    <confirm-dialog
      :show.sync="confirmDialog.show"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :item-name="confirmDialog.itemName"
      :confirm-text="confirmDialog.confirmText"
      :type="confirmDialog.type"
      :loading="confirmDialog.loading"
      @confirm="handleConfirm"
    />
  </div>
</template>

<script>
import { apiService } from '../../service/api.service'
import ConfirmDialog from '../shared/ConfirmDialog.vue'
export default {
  components: { ConfirmDialog },
  data: () => ({
    menu: false,
    isBlocked: false,
    isActive: true,
    confirmDialog: { show: false, title: '', message: '', confirmText: '', type: 'danger', loading: false },
    pendingAction: null
  }),
  mounted () {
    this.isBlocked = this.userSource.blocked
    this.isActive = this.userSource.is_active
  },
  props: { userSource: { type: Object, default: () => ({}) } },
  methods: {
    deleteUser (userId) {
      this.pendingAction = async () => {
        await apiService.put(`admin-delete-user/${userId}`)
        this.$emit('call-users')
      }
      this.confirmDialog = { show: true, title: 'Delete User?', message: 'The user will be permanently deleted.', itemName: this.userSource.name || this.userSource.email, confirmText: 'Delete', type: 'danger', loading: false }
    },
    restoreUser (userId) {
      this.pendingAction = async () => {
        await apiService.put(`admin-set-active-user/${userId}`)
        this.$emit('call-users')
      }
      this.confirmDialog = { show: true, title: 'Restore User?', message: 'The user will be restored and set to active.', itemName: this.userSource.name || this.userSource.email, confirmText: 'Restore', type: 'warning', loading: false }
    },
    saveChanges () {
      const userId = this.userSource.id
      const activeChanged = this.userSource.is_active !== this.isActive
      const blockedChanged = this.userSource.blocked !== this.isBlocked
      if (!activeChanged && !blockedChanged) return

      this.pendingAction = async () => {
        if (activeChanged) {
          await apiService.put(`admin-active-user/${userId}`, { isActive: this.isActive })
        }
        if (blockedChanged) {
          await apiService.put(`admin-blocked-user/${userId}`, { isBlocked: this.isBlocked })
        }
        this.menu = false
        this.$emit('call-users')
      }

      const parts = []
      if (activeChanged) parts.push(this.isActive ? 'active' : 'inactive')
      if (blockedChanged) parts.push(this.isBlocked ? 'blocked' : 'unblocked')
      this.confirmDialog = {
        show: true,
        title: 'Save changes?',
        message: `The user will be set to ${parts.join(' and ')}.`,
        itemName: this.userSource.name || this.userSource.email,
        confirmText: 'Save',
        type: (this.isBlocked || !this.isActive) ? 'danger' : 'info',
        loading: false
      }
    },
    async handleConfirm () {
      if (!this.pendingAction) return
      this.confirmDialog.loading = true
      try {
        await this.pendingAction()
        this.confirmDialog.show = false
      } catch (e) {
        console.error(e)
      } finally {
        this.confirmDialog.loading = false
        this.pendingAction = null
      }
    }
  }
}
</script>

<style scoped>
.ua-menu {
  background: var(--ec-surface);
  border: 1px solid var(--ec-border);
  border-radius: var(--ec-radius);
  box-shadow: var(--ec-shadow-lg);
  min-width: 280px;
  overflow: hidden;
}
.ua-menu__header {
  display: flex; align-items: center; gap: 12px;
  padding: 16px; border-bottom: 1px solid var(--ec-border);
  background: var(--ec-bg);
}
.ua-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  background: var(--ec-primary); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: .95rem; font-weight: 700; flex-shrink: 0;
}
.ua-menu__info { min-width: 0; }
.ua-menu__name  { font-size: .875rem; font-weight: 600; color: var(--ec-text-1); margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ua-menu__email { font-size: .75rem; color: var(--ec-text-3); margin: 2px 0 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ua-menu__body { padding: 12px 16px; }
.ua-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.ua-toggle-label { font-size: .85rem; font-weight: 600; color: var(--ec-text-1); margin: 0; }
.ua-toggle-hint  { font-size: .72rem; color: var(--ec-text-3); margin: 2px 0 0; }
::v-deep .ua-switch { margin: 0 !important; padding: 0 !important; }

.ua-menu__footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-top: 1px solid var(--ec-border); background: var(--ec-bg);
}
.ua-danger-btn {
  display: inline-flex; align-items: center;
  padding: 5px 10px; border-radius: 6px;
  font-size: .75rem; font-weight: 600;
  background: var(--ec-danger-bg); color: var(--ec-danger);
  border: 1px solid #fca5a5; cursor: pointer; transition: var(--ec-transition);
}
.ua-danger-btn:hover { background: var(--ec-danger); color: #fff; border-color: var(--ec-danger); }
.ua-restore-btn {
  display: inline-flex; align-items: center;
  padding: 5px 10px; border-radius: 6px;
  font-size: .75rem; font-weight: 600;
  background: var(--ec-warning-bg); color: var(--ec-warning);
  border: 1px solid #fcd34d; cursor: pointer; transition: var(--ec-transition);
}
.ua-restore-btn:hover { background: var(--ec-warning); color: #fff; border-color: var(--ec-warning); }
</style>
